import { useEffect, useState } from "react";
import { ArrowRight, Check, CircleNotch, Sparkle, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useEntitlements } from "../lib/entitlements";
import { LiveDot } from "./LiveTicker";

// Compact plan-management panel dropped into DashSettings so owners can
// upgrade / downgrade / cancel without leaving the app. The tier modal
// re-uses /api/plans/checkout so Stripe handles proration + card details.
export default function PlanManager() {
  const ent = useEntitlements();
  const [catalog, setCatalog] = useState({ plans: {}, one_time_addons: {} });
  const [modal, setModal] = useState(null); // null | "change" | "confirmCancel"
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (ent?.billing_interval) setBilling(ent.billing_interval);
  }, [ent?.billing_interval]);

  const currentKey = ent?.plan_key || null;
  const currentPlan = currentKey ? catalog.plans?.[currentKey] : null;
  const wg = catalog.one_time_addons?.a2p_whiteglove;
  const purchasedWG = (ent?.addons || []).includes("a2p_whiteglove");

  const buyPlan = async (planKey) => {
    setBusy(planKey);
    try {
      const r = await api.post("/plans/checkout", {
        plan_key: planKey, billing, origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const buyWhiteGlove = async () => {
    setBusy("whiteglove");
    try {
      const r = await api.post("/addons/whiteglove/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const cancelPlan = async () => {
    setBusy("cancel");
    try {
      await api.post("/plans/cancel", {});
      toast.success("Plan canceled. You're on the Free tier.");
      ent.refresh && ent.refresh();
      setModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
    setBusy(null);
  };

  return (
    <div className="bg-white ring-1 ring-black/5 rounded-2xl p-6 mb-6" data-testid="settings-plan-manager">
      {/* Header — current plan */}
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#5C6B61] font-bold mb-1 flex items-center gap-2">
            <LiveDot color={currentPlan ? "#1E3A2F" : "#F5C543"} /> Your plan
          </div>
          <div className="font-heading text-2xl text-[#121914] leading-tight">
            {currentPlan ? currentPlan.name : "Waitlist Core"}
            {currentPlan ? (
              <span className="text-sm text-[#5C6B61] font-normal ml-2">
                ${billing === "annual" ? currentPlan.annual_price : currentPlan.monthly_price}
                /{billing === "annual" ? "yr" : "mo"}
              </span>
            ) : (
              <span className="text-sm text-[#5C6B61] font-normal ml-2">Free forever</span>
            )}
          </div>
          {currentPlan ? (
            <p className="text-xs text-[#5C6B61] mt-1 leading-snug max-w-md">{currentPlan.description}</p>
          ) : (
            <p className="text-xs text-[#5C6B61] mt-1 leading-snug max-w-md">Live queue, walk-ins, SMS table-ready alerts. Upgrade anytime to add Mimi on chat and phone.</p>
          )}
          {ent?.is_trial && (
            <div className="text-[10px] font-mono text-[#D96C5B] mt-2 inline-flex items-center gap-1.5 bg-[#F5C543]/20 px-2 py-1 rounded-full">
              <Sparkle size={10} weight="fill" /> Trial · all features until {new Date(ent.trial_expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setModal("change")}
            data-testid="plan-change-btn"
            className="inline-flex items-center gap-1.5 bg-[#0E1512] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#1E3A2F] transition-colors"
          >
            {currentKey ? "Change plan" : "Upgrade"} <ArrowRight size={14} weight="bold" />
          </button>
          {currentKey && (
            <button
              onClick={() => setModal("confirmCancel")}
              data-testid="plan-cancel-btn"
              className="text-xs text-[#A5382A] hover:underline px-2"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Features included in current plan */}
      {currentPlan && (
        <div className="border-t border-black/5 pt-3">
          <div className="text-[10px] tracking-widest uppercase text-[#5C6B61] font-bold mb-2">Included</div>
          <div className="flex flex-wrap gap-1.5">
            {(currentPlan.highlights || []).map((h) => (
              <span key={h} className="inline-flex items-center gap-1 text-[11px] text-[#121914] bg-[#C2D1C1]/40 px-2 py-1 rounded-full">
                <Check size={10} weight="bold" color="#1E3A2F" /> {h}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* One-time white-glove — only if not purchased */}
      {wg && !purchasedWG && (
        <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-[#F5C543] flex items-center justify-center shrink-0">
              <Sparkle size={16} color="#0E1512" weight="fill" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#121914]">{wg.name} <span className="text-xs text-[#5C6B61] font-normal">· one-time ${wg.price}</span></div>
              <p className="text-[11px] text-[#5C6B61] leading-snug mt-0.5 max-w-md">{wg.description}</p>
            </div>
          </div>
          <button
            onClick={buyWhiteGlove}
            disabled={busy === "whiteglove"}
            data-testid="plan-whiteglove-btn"
            className="inline-flex items-center gap-1.5 bg-[#F5C543] text-[#0E1512] text-sm font-bold px-4 py-2 rounded-full hover:bg-[#F7D470] transition-colors disabled:opacity-60"
          >
            {busy === "whiteglove" ? <CircleNotch className="animate-spin" size={14} /> : <>Add for ${wg.price} <ArrowRight size={14} weight="bold" /></>}
          </button>
        </div>
      )}
      {purchasedWG && (
        <div className="mt-4 pt-4 border-t border-black/5 text-[11px] text-[#5C6B61] font-mono flex items-center gap-1.5">
          <Check size={12} weight="bold" color="#1E3A2F" /> White-glove SMS setup purchased — our team is on it
        </div>
      )}

      {/* CHANGE-PLAN MODAL */}
      {modal === "change" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0E1512]/60 backdrop-blur-sm" onClick={() => setModal(null)} data-testid="plan-change-modal">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setModal(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center" data-testid="plan-modal-close">
              <X size={16} />
            </button>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-bold mb-2">Change plan</div>
            <h2 className="font-heading text-3xl text-[#121914] leading-tight mb-1">
              Pick your <span className="honey-bar italic text-[#D96C5B]">buzz</span>.
            </h2>
            <p className="text-sm text-[#5C6B61] mb-5">Stripe handles proration — you're only charged the difference.</p>

            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#F7F6F2] ring-1 ring-black/10 mb-4">
              {["monthly", "annual"].map((b) => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  data-testid={`modal-billing-${b}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                    billing === b ? "bg-[#0E1512] text-white" : "text-[#5C6B61] hover:text-[#121914]"
                  }`}
                >
                  {b === "annual" ? "Annual" : "Monthly"}
                  {b === "annual" && (
                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${billing === "annual" ? "bg-[#F5C543] text-[#0E1512]" : "bg-[#F5C543]/20 text-[#0E1512]"}`}>2 mo free</span>
                  )}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              {Object.values(catalog.plans || {}).map((plan) => {
                const isCurrent = plan.key === currentKey && (ent?.billing_interval || "monthly") === billing;
                const isRec = plan.key === "complete";
                const price = billing === "annual" ? plan.annual_price : plan.monthly_price;
                return (
                  <div
                    key={plan.key}
                    className={`rounded-xl p-4 flex flex-col ${
                      isRec ? "bg-[#0E1512] text-white ring-2 ring-[#F5C543]" : "bg-white ring-1 ring-black/10"
                    }`}
                    data-testid={`modal-plan-${plan.key}`}
                  >
                    <div className={`text-[10px] tracking-widest uppercase font-bold mb-1 ${isRec ? "text-[#F5C543]" : "text-[#D96C5B]"}`}>{plan.name}</div>
                    <div className={`font-heading text-lg mb-1 ${isRec ? "text-white" : "text-[#121914]"}`}>{plan.tagline}</div>
                    <div className="mb-3 flex items-baseline gap-1">
                      <span className={`font-heading text-2xl ${isRec ? "text-white" : "text-[#121914]"}`}>${price}</span>
                      <span className={`text-[10px] font-mono ${isRec ? "text-[#C2D1C1]" : "text-[#5C6B61]"}`}>/{billing === "annual" ? "yr" : "mo"}</span>
                    </div>
                    <ul className={`space-y-1 text-[11px] flex-1 mb-3 ${isRec ? "text-white" : "text-[#121914]"}`}>
                      {(plan.highlights || []).slice(0, 4).map((h) => (
                        <li key={h} className="flex items-start gap-1.5">
                          <Check size={11} weight="bold" color={isRec ? "#F5C543" : "#1E3A2F"} className="mt-0.5 shrink-0" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => buyPlan(plan.key)}
                      disabled={isCurrent || busy === plan.key}
                      data-testid={`modal-plan-${plan.key}-cta`}
                      className={`w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full transition-all disabled:opacity-60 ${
                        isRec
                          ? "bg-[#F5C543] text-[#0E1512] hover:bg-[#F7D470]"
                          : "bg-[#0E1512] text-white hover:bg-[#1E3A2F]"
                      }`}
                    >
                      {isCurrent ? "Current plan" : busy === plan.key ? "Redirecting…" : `Switch to ${plan.name}`}
                      {!isCurrent && busy !== plan.key && <ArrowRight size={12} weight="bold" />}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-[#5C6B61] mt-4 text-center">
              Or <button onClick={() => { setModal("confirmCancel"); }} className="underline text-[#A5382A] hover:no-underline" data-testid="modal-open-cancel">cancel and drop to Free tier</button> — no fee, no questions.
            </p>
          </div>
        </div>
      )}

      {/* CANCEL CONFIRM MODAL */}
      {modal === "confirmCancel" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0E1512]/60 backdrop-blur-sm" onClick={() => setModal(null)} data-testid="plan-cancel-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#A5382A] font-bold mb-2">Cancel plan</div>
            <h3 className="font-heading text-2xl text-[#121914] mb-2">Are you sure?</h3>
            <p className="text-sm text-[#5C6B61] mb-5 leading-snug">
              You'll drop to the Free waitlist tier immediately. Mimi will stop answering the phone. Your Twilio number keeps working — you'll just fall back to voicemail like before. Waitlist keeps working, forever.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-full text-sm text-[#121914] border border-black/10 hover:border-black/30 transition-colors">Keep plan</button>
              <button
                onClick={cancelPlan}
                disabled={busy === "cancel"}
                data-testid="plan-cancel-confirm"
                className="inline-flex items-center gap-1.5 bg-[#A5382A] text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-[#8B2E22] transition-colors disabled:opacity-60"
              >
                {busy === "cancel" ? <><CircleNotch className="animate-spin" size={14} /> Canceling…</> : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
